import React from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from 'reactstrap'
import { CreateReservePlanRequest } from '../../../../api-dtos/support/merchant/reserve-plan.dto.interface'

interface ReservePlanModalsProps {
  showCreateModal: boolean
  showEditModal: boolean
  showDeleteModal: boolean

  formData: CreateReservePlanRequest
  submitting: boolean

  onCloseCreateModal: () => void
  onCloseEditModal: () => void
  onCloseDeleteModal: () => void

  onFormDataChange: (data: CreateReservePlanRequest) => void
  onCreatePlan: () => void
  onUpdatePlan: () => void
  onDeletePlan: () => void
}

const ReservePlanModals: React.FC<ReservePlanModalsProps> = ({
  showCreateModal,
  showEditModal,
  showDeleteModal,
  formData,
  submitting,
  onCloseCreateModal,
  onCloseEditModal,
  onCloseDeleteModal,
  onFormDataChange,
  onCreatePlan,
  onUpdatePlan,
  onDeletePlan,
}) => {
  return (
    <>
      {/* Create Reserve Plan Modal */}
      <Modal isOpen={showCreateModal} toggle={onCloseCreateModal}>
        <ModalHeader toggle={onCloseCreateModal}>
          Create Reserve Plan
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="percent">Reserve Percentage (1-100%)</Label>
              <Input
                type="number"
                id="percent"
                min="1"
                max="100"
                value={formData.percent}
                onChange={(e) =>
                  onFormDataChange({
                    ...formData,
                    percent: parseInt(e.target.value),
                  })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="minimumDays">Minimum Days (1-180)</Label>
              <Input
                type="number"
                id="minimumDays"
                min="1"
                max="180"
                value={formData.minimumDays}
                onChange={(e) =>
                  onFormDataChange({
                    ...formData,
                    minimumDays: parseInt(e.target.value),
                  })
                }
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onCloseCreateModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={onCreatePlan} disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : 'Create Plan'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Reserve Plan Modal */}
      <Modal isOpen={showEditModal} toggle={onCloseEditModal}>
        <ModalHeader toggle={onCloseEditModal}>Edit Reserve Plan</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="edit-percent">Reserve Percentage (1-100%)</Label>
              <Input
                type="number"
                id="edit-percent"
                min="1"
                max="100"
                value={formData.percent}
                onChange={(e) =>
                  onFormDataChange({
                    ...formData,
                    percent: parseInt(e.target.value),
                  })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label for="edit-minimumDays">Minimum Days (1-180)</Label>
              <Input
                type="number"
                id="edit-minimumDays"
                min="1"
                max="180"
                value={formData.minimumDays}
                onChange={(e) =>
                  onFormDataChange({
                    ...formData,
                    minimumDays: parseInt(e.target.value),
                  })
                }
              />
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onCloseEditModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={onUpdatePlan} disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : 'Update Plan'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Reserve Plan Modal */}
      <Modal isOpen={showDeleteModal} toggle={onCloseDeleteModal}>
        <ModalHeader toggle={onCloseDeleteModal}>
          Delete Reserve Plan
        </ModalHeader>
        <ModalBody>
          <p>Are you sure you want to delete this reserve plan?</p>
          <p className="text-warning">
            <strong>Note:</strong> This will disable the plan and stop new
            reserves, but existing reserves will remain until their release
            dates.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onCloseDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={onDeletePlan} disabled={submitting}>
            {submitting ? <Spinner size="sm" /> : 'Delete Plan'}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default ReservePlanModals
